// =============================================================================
// LP Express — Problem / Solution (PAS / unique-mechanism) template content
// model.
//
// A "Problem / Solution" landing page is a long-scrolling PAS (Problem-Agitate-
// Solution) sales page built around a "unique mechanism" (ported from the
// template library's sales.problem-solution): a problem-framing hero (eyebrow +
// headline + subhead) → a problem-agitation section → a "why the usual fixes
// fail" list → the mechanism reveal (paragraphs + an AI-generated diagram
// image) → the solution reveal ("Meet [product]" + paragraphs + a benefits grid
// + the brand's REAL product photo) → a proof band (persona-voice testimonial
// quotes, swappable for REAL reviews via the review picker) → a bordered offer
// box → a guarantee block → an FAQ accordion → a legal disclaimer → a sticky
// bottom CTA bar (mobile).
//
// The whole page's COPY is generated in a single shot by a dedicated Sim
// Studio workflow (SIM_LP_PROBLEM_SOLUTION_URL — a placeholder id until the
// workflow is built), then SPLIT by the server into per-section
// generatedContent keys (psHero, psProblem, psWhyFail, psMechanism, psSolution,
// psProof, psOffer, psGuarantee, psFaq, psDisclaimer, psStickyCta — see
// `splitLpProblemSolutionContent`). Those keys back the ordered, reorderable
// section model in shared/lp-express-sections.ts
// (LP_PROBLEM_SOLUTION_SECTION_TYPES), so the page flows through the SAME
// sections pipeline as the product-hero: getLpSections order, moveSection
// reorder, per-section hide/delete, and per-section copy regeneration (the
// single workflow re-runs and the server merges back ONLY the requested
// section). Copy is edited in place through the SAME generatedContent
// path-based `setText` ops the other templates use — e.g.
// ["psWhyFail","items",0,"title"].
//
// IMAGERY: the mechanism section carries ONE AI-generated diagram/illustration
// slot (LP_PROBLEM_SOLUTION_IMAGE_SLOTS below →
// generatedContent.problemSolutionImages) rendered through the standard LP
// Express single-slot image pipeline (per-slot pending status, retry, startup
// resume). The solution section shows the brand's REAL product photo
// (swappable only via the media picker/upload — never AI-generated). Proof
// quotes come from the brand's REAL review data via the review picker when
// available — the workflow's copy is a clearly-editable persona-voice
// placeholder. Prices, discounts and savings figures are DERIVED from the real
// product/offer/sale data at render time — the workflow's copy never invents
// numbers, stats, clinical claims, or dollar amounts, and discount framing only
// appears when a real active discount exists on the run.
//
// This module is pure + dependency-free so it runs identically on the client
// (renderer + editor) and the server (generation/normalization).
//
// ─── Sim workflow contract (SIM_LP_PROBLEM_SOLUTION_URL) ─────────────────────
//
// INPUT (top-level fields on the Start block — called with wrapInput=false,
// identical to the quiz/listicle/advertorial/product-hero envelopes):
//   {
//     "brandInfo":      string  // serialized brand doctrine/profile
//     "personaInfo":    string  // serialized persona persuasion profile
//     "productInfo":    string  // serialized product/offer attributes
//     "narrativeAngle": string  // serialized narrative angle
//     "offerInfo":      string  // serialized offer/pricing/sale context
//     "feedback":       string  // optional free-form regeneration steering
//     "currentCopy":    string  // optional JSON of the CURRENT page copy
//                               // (set on regeneration so the workflow
//                               // refines instead of starting from scratch)
//   }
//
// OUTPUT: a single JSON object (may be ```json-fenced under output.content —
// the extractor strips fences) matching LpProblemSolutionContent below.
// snake_case aliases are accepted for every camelCase key. Guidance for the
// workflow:
//   • Hero: a problem-framing `headline` (name the loop/pattern, not the
//     product), a short `eyebrow` kicker and a `subhead` that reframes the
//     problem as a mechanism ("it's not X — it's Y").
//   • `problem.paragraphs`: 2–3 agitation paragraphs in persona voice.
//   • `whyFail.items`: 3–5 { title, why } cards — each a common fix and the
//     one-line reason it doesn't touch the mechanism.
//   • `mechanism.paragraphs`: 2–3 paragraphs naming and explaining the unique
//     mechanism, ending with how the product was built around it.
//   • `solution`: a "Meet [product]" `heading`, 1–2 `paragraphs`, and
//     `benefits.items`: 3–6 cards ({ title, body }) — the icon is chosen by
//     the renderer (lucide), so any `ic` field is ignored.
//   • `proof.items`: 2–3 testimonial cards ({ name, role, text, rating }) in
//     persona voice — clearly editable placeholders that operators are
//     expected to replace with REAL reviews via the review picker.
//   • `proof.stats`: MUST be omitted / left empty by the workflow — stat
//     cards ({ n, l }) are derived from the brand's REAL review aggregates at
//     render time or typed in by the operator. Never invent numbers.
//   • `offer`: an offer-box `heading`, one-line `subhead`, CTA `ctaText`, and
//     a `terms` reassurance line. NO prices — derived from real data.
//   • `guarantee`: sealTop/sealBottom (e.g. "90" / "Day Guarantee"), heading,
//     body.
//   • `faq.items`: 4–6 Q&A pairs.
//   • Do NOT put prices, percentages, statistics, review counts, clinical
//     claims or dollar amounts in ANY copy field — those are derived from real
//     data at render time or omitted.
// =============================================================================

export interface LpProblemSolutionWhyFailItem {
  title: string;
  why: string; // one-line reason the fix fails ("" = none)
}

export interface LpProblemSolutionBenefit {
  ic: string; // raw icon hint from the workflow (ignored — renderer uses lucide)
  title: string;
  body: string;
}

// One proof-band quote card. Generated in persona voice by the Sim workflow
// and fully editable in the builder. When the brand has REAL review data, each
// card can be swapped for a real review via the standard review picker (the
// picked review is snapshotted on reviewOverrides and wins over the blob item).
export interface LpProblemSolutionQuote {
  name: string;
  role: string; // e.g. "Verified buyer" ("" = none)
  text: string;
  rating: number; // 1–5 stars
}

// One proof-band stat card ({ n: the big number/value, l: its label } —
// matching the source template's proof.stats.items shape). FTC-SAFE BY
// CONSTRUCTION: the Sim workflow is instructed to NEVER output stats (any it
// returns are still normalized, but the contract forbids invented numbers).
// Stats come from REAL data only — seeded/derived from the brand's real
// review aggregates at render time, or typed in by the operator (fully
// editable in the builder). The band hides when no real stats exist.
export interface LpProblemSolutionStat {
  n: string; // stat value, e.g. "4.8★" or "12,000+"
  l: string; // stat label, e.g. "average rating"
}

export interface LpProblemSolutionFaq {
  q: string;
  a: string;
}

export interface LpProblemSolutionContent {
  eyebrow: string; // small kicker above the headline ("" = none)
  headline: string; // problem-framing hero headline
  subhead: string; // mechanism-reframing sub-line ("" = none)
  problem: { heading: string; paragraphs: string[] };
  whyFail: { heading: string; items: LpProblemSolutionWhyFailItem[] };
  mechanism: { heading: string; paragraphs: string[] };
  solution: {
    heading: string;
    paragraphs: string[];
    benefits: LpProblemSolutionBenefit[];
  };
  proof: {
    heading: string;
    stats: LpProblemSolutionStat[];
    items: LpProblemSolutionQuote[];
  };
  offer: { heading: string; subhead: string; ctaText: string; terms: string };
  guarantee: {
    sealTop: string;
    sealBottom: string;
    heading: string;
    body: string;
  };
  faq: { heading: string; items: LpProblemSolutionFaq[] };
  disclaimer: string;
  stickyCtaText: string; // sticky bottom bar CTA label
}

// ── Normalization ────────────────────────────────────────────────────────────

function asObj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
}

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : typeof v === "number" ? String(v) : fallback;
}

function strArray(v: unknown, cap = 20): string[] {
  return Array.isArray(v)
    ? v.filter((x) => typeof x === "string" && (x as string).trim()).slice(0, cap)
    : [];
}

// Accept either a plain string array or the template-library's wrapped
// { items: [...] } / { paragraphs: [...] } object.
function paragraphsArray(v: unknown, cap: number): string[] {
  if (Array.isArray(v)) return strArray(v, cap);
  const o = asObj(v);
  return strArray(o.paragraphs ?? o.items, cap);
}

// Normalize a raw problem-solution payload (e.g. from the Sim workflow, which
// may use snake_case) into the canonical LpProblemSolutionContent shape.
// Missing fields become empty strings/arrays so the renderer/editor never
// crash — the caller decides whether an empty result counts as a generation
// failure (isLpProblemSolutionContentUsable). IMPORTANT: any new editable
// field must be added BOTH here and to the interfaces above, or the renderer
// will drop it.
export function normalizeLpProblemSolutionContent(
  raw: unknown,
): LpProblemSolutionContent {
  const o = asObj(raw);
  const meta = asObj(o.meta);

  const prSrc = asObj(o.problem);
  const problem = {
    heading: str(prSrc.heading),
    paragraphs: paragraphsArray(prSrc.paragraphs ?? prSrc.body, 5),
  };

  const wfSrc = asObj(o.whyFail ?? o.why_fail ?? o.whyFixesFail);
  const whyFail = {
    heading: str(wfSrc.heading),
    items: (Array.isArray(wfSrc.items) ? wfSrc.items : [])
      .map((it) => {
        const io = asObj(it);
        return {
          title: str(io.title ?? io.heading ?? io.fix),
          why: str(io.why ?? io.reason ?? io.body),
        };
      })
      .filter((it) => it.title || it.why)
      .slice(0, 6),
  };

  const meSrc = asObj(o.mechanism);
  const mechanism = {
    heading: str(meSrc.heading),
    paragraphs: paragraphsArray(meSrc.paragraphs ?? meSrc.body, 5),
  };

  const soSrc = asObj(o.solution);
  const benefitsSrc = asObj(soSrc.benefits);
  const solution = {
    heading: str(soSrc.heading),
    paragraphs: paragraphsArray(soSrc.paragraphs ?? soSrc.body, 4),
    benefits: (Array.isArray(soSrc.benefits)
      ? soSrc.benefits
      : Array.isArray(benefitsSrc.items)
        ? benefitsSrc.items
        : []
    )
      .map((b) => {
        const bo = asObj(b);
        return {
          ic: str(bo.ic ?? bo.icon),
          title: str(bo.title ?? bo.h ?? bo.heading),
          body: str(bo.body ?? bo.p ?? bo.text),
        };
      })
      .filter((b) => b.title || b.body)
      .slice(0, 6),
  };

  const pfSrc = asObj(o.proof);
  const quotesSrc = asObj(pfSrc.quotes);
  const statsSrc = asObj(pfSrc.stats);
  const proof = {
    heading: str(pfSrc.heading),
    // Source-template proof.stats.items shape ({ n, l }) — accepted from
    // stored/edited content. The Sim workflow contract forbids inventing
    // numbers, so generated pages start with [] and stats are seeded from
    // real review data or entered by the operator.
    stats: (Array.isArray(pfSrc.stats)
      ? pfSrc.stats
      : Array.isArray(statsSrc.items)
        ? statsSrc.items
        : []
    )
      .map((s) => {
        const so = asObj(s);
        return {
          n: str(so.n ?? so.value ?? so.number ?? so.stat),
          l: str(so.l ?? so.label ?? so.caption),
        };
      })
      .filter((s) => s.n && s.l)
      .slice(0, 4),
    items: (Array.isArray(pfSrc.items)
      ? pfSrc.items
      : Array.isArray(quotesSrc.items)
        ? quotesSrc.items
        : Array.isArray(o.testimonials)
          ? o.testimonials
          : []
    )
      .map((t) => {
        const to = asObj(t);
        const ratingRaw = Number(to.rating ?? to.stars ?? 5);
        return {
          name: str(to.name ?? to.reviewer ?? to.author),
          role: str(to.role ?? to.title ?? to.meta),
          text: str(to.text ?? to.quote ?? to.review_text ?? to.reviewText),
          rating: Number.isFinite(ratingRaw)
            ? Math.max(1, Math.min(5, Math.round(ratingRaw)))
            : 5,
        };
      })
      .filter((t) => t.text)
      .slice(0, 3),
  };

  const ofSrc = asObj(o.offer);
  const offer = {
    heading: str(ofSrc.heading),
    subhead: str(ofSrc.subhead ?? ofSrc.dek),
    ctaText: str(ofSrc.ctaText ?? ofSrc.cta_text) || "Get started",
    terms: str(ofSrc.terms ?? ofSrc.ctaSub ?? ofSrc.cta_sub),
  };

  const guSrc = asObj(o.guarantee);
  const guarantee = {
    sealTop: str(guSrc.sealTop ?? guSrc.seal_top),
    sealBottom: str(guSrc.sealBottom ?? guSrc.seal_bottom),
    heading: str(guSrc.heading),
    body: str(guSrc.body ?? guSrc.text),
  };

  const faqSrc = asObj(o.faq);
  const faq = {
    heading: str(faqSrc.heading),
    items: (Array.isArray(faqSrc.items) ? faqSrc.items : [])
      .map((f) => {
        const fo = asObj(f);
        return {
          q: str(fo.q ?? fo.question),
          a: str(fo.a ?? fo.answer ?? fo.body),
        };
      })
      .filter((f) => f.q || f.a)
      .slice(0, 8),
  };

  const stSrc = asObj(o.sticky ?? o.sticky_bar ?? o.stickyBar);

  return {
    eyebrow: str(o.eyebrow ?? meta.eyebrow),
    headline: str(o.headline ?? meta.headline),
    subhead: str(o.subhead ?? o.dek ?? o.subheadline ?? meta.subhead),
    problem,
    whyFail,
    mechanism,
    solution,
    proof,
    offer,
    guarantee,
    faq,
    disclaimer: str(o.disclaimer),
    stickyCtaText:
      str(o.stickyCtaText ?? o.sticky_cta_text ?? str(stSrc.text)) ||
      offer.ctaText,
  };
}

// ── Per-section split / assembly ─────────────────────────────────────────────
// The single-workflow output is split into one generatedContent key per
// section block (matching LP_PROBLEM_SOLUTION_SECTION_TYPES in
// shared/lp-express-sections.ts) so the page participates in the ordered
// sections model: reorder, hide/delete, and per-section copy regeneration.

// Hero copy slice.
export interface LpProblemSolutionHero {
  eyebrow: string;
  headline: string;
  subhead: string;
}

// The per-section generatedContent keys, in canonical page order. Must stay in
// lockstep with LP_PROBLEM_SOLUTION_SECTION_TYPES in
// shared/lp-express-sections.ts.
export const LP_PROBLEM_SOLUTION_BLOCK_KEYS = [
  "psHero",
  "psProblem",
  "psWhyFail",
  "psMechanism",
  "psSolution",
  "psProof",
  "psOffer",
  "psGuarantee",
  "psFaq",
  "psDisclaimer",
  "psStickyCta",
] as const;
export type LpProblemSolutionBlockKey =
  (typeof LP_PROBLEM_SOLUTION_BLOCK_KEYS)[number];

// Split a normalized whole-page content object into its per-section
// generatedContent keys. Inverse of assembleLpProblemSolutionContent.
export function splitLpProblemSolutionContent(
  content: LpProblemSolutionContent,
): Record<LpProblemSolutionBlockKey, unknown> {
  const hero: LpProblemSolutionHero = {
    eyebrow: content.eyebrow,
    headline: content.headline,
    subhead: content.subhead,
  };
  return {
    psHero: hero,
    psProblem: content.problem,
    psWhyFail: content.whyFail,
    psMechanism: content.mechanism,
    psSolution: content.solution,
    psProof: content.proof,
    psOffer: content.offer,
    psGuarantee: content.guarantee,
    psFaq: content.faq,
    psDisclaimer: { text: content.disclaimer },
    psStickyCta: { ctaText: content.stickyCtaText },
  };
}

// Re-assemble the whole-page content shape from a generatedContent object that
// carries the per-section keys. Tolerant of missing/partial keys (returns
// normalized empties) — callers gate usability via
// isLpProblemSolutionContentUsable. Used by the renderer/editor to read one
// canonical shape, and by the server to build `currentCopy` for regeneration.
export function assembleLpProblemSolutionContent(
  gc: unknown,
): LpProblemSolutionContent {
  const o = asObj(gc);
  const hero = asObj(o.psHero);
  const disclaimer = asObj(o.psDisclaimer);
  const sticky = asObj(o.psStickyCta);
  return normalizeLpProblemSolutionContent({
    eyebrow: hero.eyebrow,
    headline: hero.headline,
    subhead: hero.subhead,
    problem: o.psProblem,
    whyFail: o.psWhyFail,
    mechanism: o.psMechanism,
    solution: o.psSolution,
    proof: o.psProof,
    offer: o.psOffer,
    guarantee: o.psGuarantee,
    faq: o.psFaq,
    disclaimer: (disclaimer as { text?: unknown }).text,
    stickyCtaText: (sticky as { ctaText?: unknown }).ctaText,
  });
}

// True when a generatedContent object carries the per-section problem-solution
// keys with enough structure to render (see
// isLpProblemSolutionContentUsable).
export function hasUsableLpProblemSolutionSections(gc: unknown): boolean {
  const o = asObj(gc);
  if (!o.psHero) return false;
  return isLpProblemSolutionContentUsable(assembleLpProblemSolutionContent(o));
}

// True when a normalized problem-solution page carries enough structure to
// render a real PAS page (a headline plus at least three substantive
// supporting blocks across the problem paragraphs, why-fail items, mechanism
// paragraphs, solution benefits and FAQ). Used by the generator to reject an
// empty Sim response instead of persisting a blank page.
export function isLpProblemSolutionContentUsable(
  content: LpProblemSolutionContent | null | undefined,
): boolean {
  if (!content) return false;
  const hasHeadline = !!content.headline;
  const supporting =
    content.problem.paragraphs.length +
    content.whyFail.items.length +
    content.mechanism.paragraphs.length +
    content.solution.benefits.length +
    content.faq.items.length;
  return hasHeadline && supporting >= 3;
}

// ── AI mechanism-diagram image slot ──────────────────────────────────────────
// ONE AI-generated diagram/illustration for the mechanism section, stored
// under `generatedContent.problemSolutionImages` as
// [{ key, imageUrl, imageStatus, altText }] and generated per-slot through the
// standard LP Express single-slot image pipeline (same shape as
// productHeroImages, so retry + startup-resume ride the existing plumbing).
// The solution section's product image is always a REAL product photo
// (media picker / mediaOverrides) — never AI-generated.
export const LP_PROBLEM_SOLUTION_IMAGE_SLOTS: {
  key: string;
  variantHint: string;
}[] = [
  {
    key: "mechanism-diagram",
    variantHint:
      "mechanism-diagram — a clean, editorial explainer illustration of the underlying cause/mechanism described by the narrative angle (a cycle, loop, or before/after schematic rendered as a soft, modern infographic-style visual); muted brand-adjacent palette on a light background, generous whitespace, NO text, NO words, NO labels, NO numbers anywhere in the image, no charts with fake data — purely visual shapes, arrows and iconography",
  },
];
